import React from 'react'
import { Skeleton } from 'components/skeleton'
import './content-loading.css'

const ReaderLines = ({ rows = 4 }: { rows?: number }) => (
	<div className="reader-placeholder-lines">
		{Array.from({ length: rows }, (_, row) => (
			<div key={row}>
				<Skeleton
					className="h-[18px] motion-reduce:animate-none"
					style={{ width: row % 2 ? '64%' : '82%' }}
				/>
				<Skeleton className="h-3 w-[94%] motion-reduce:animate-none" />
			</div>
		))}
	</div>
)

/** Mirror the CMS layout while its authenticated iframe is opening. Keep measurements
 * in sync with the CMS's shared reader classes, not a generic dashboard table. */
const ReaderLoading = ({ path, label }: { path: string; label: string }) => {
	const url = new URL(path, 'https://reader.invalid')
	const segments = url.pathname.split('/').filter(Boolean).slice(1)
	const kind = url.searchParams.get('q')?.trim()
		? 'search'
		: segments[0] === 'changelog'
		? segments.length > 1
			? 'release'
			: 'changelog'
		: segments.length > 1
		? 'article'
		: segments.length
		? 'category'
		: 'home'
	const detail = kind === 'article' || kind === 'release'
	return (
		<div
			role="status"
			aria-label={`Opening ${label}…`}
			className="content-reader-loading"
			data-reader-loading={kind}
		>
			<span className="sr-only">Opening {label}…</span>
			<header className="site-header" data-skeleton-section="header">
				<Skeleton className="h-7 w-40 motion-reduce:animate-none" />
				<Skeleton className="h-9 w-60 max-w-[40vw] motion-reduce:animate-none" />
			</header>
			<div className="site-body">
				<aside className="site-sidebar" data-skeleton-section="navigation">
					<div className="category-tree">
						{Array.from({ length: 7 }, (_, row) => (
							<div key={row}>
								<Skeleton
									className="h-4 motion-reduce:animate-none"
									style={{ width: row % 2 ? '64%' : '84%' }}
								/>
							</div>
						))}
					</div>
				</aside>
				<main data-skeleton-section="content">
					{detail ? (
						<div
							className={kind === 'article' ? 'article-layout' : 'release-page'}
						>
							<article
								className={`article-content${
									kind === 'release' ? ' release-content' : ''
								}`}
							>
								<Skeleton className="mb-4 h-4 w-[55%] motion-reduce:animate-none" />
								<Skeleton className="h-10 w-[82%] motion-reduce:animate-none" />
								<ReaderLines rows={2} />
								<Skeleton className="mt-4 mb-8 h-8 w-[180px] motion-reduce:animate-none" />
								<ReaderLines rows={5} />
							</article>
							{kind === 'article' && (
								<aside className="article-toc">
									<ReaderLines />
								</aside>
							)}
						</div>
					) : kind === 'search' ? (
						<div className="page-content">
							<Skeleton className="h-10 w-[70%] motion-reduce:animate-none" />
							<ReaderLines rows={5} />
						</div>
					) : (
						<>
							<section className={`hero${kind === 'home' ? ' home-hero' : ''}`}>
								<div
									className={`hero-content${
										kind === 'home' ? '' : ' scoped-hero-content'
									}`}
								>
									<div>
										<Skeleton
											className={`h-11 w-[82%] motion-reduce:animate-none${
												kind === 'home' ? ' mx-auto' : ''
											}`}
										/>
										<ReaderLines rows={2} />
									</div>
									{kind !== 'home' && (
										<Skeleton className="h-11 motion-reduce:animate-none" />
									)}
								</div>
							</section>
							{kind === 'home' ? (
								<section className="home-band">
									<div className="home-band-inner">
										<ReaderLines rows={2} />
										<ReaderLines />
									</div>
								</section>
							) : (
								<div
									className={
										kind === 'changelog'
											? 'release-index'
											: 'page-content category-content'
									}
								>
									<ReaderLines rows={5} />
								</div>
							)}
						</>
					)}
				</main>
			</div>
		</div>
	)
}

/** Section placeholders keep the iframe opening from blocking the entire surface. */
export const ContentLoading = ({
	reader = false,
	readerPath = '/docs',
	label
}: {
	reader?: boolean
	readerPath?: string
	label: string
}) =>
	reader ? (
		<ReaderLoading path={readerPath} label={label} />
	) : (
		<div
			role="status"
			aria-label={`Opening ${label}…`}
			className="h-full w-full overflow-hidden p-6"
		>
			<span className="sr-only">Opening {label}…</span>
			<div
				data-skeleton-section="header"
				className="mb-8 flex items-center justify-between gap-6"
			>
				<Skeleton className="h-7 w-48 motion-reduce:animate-none" />
				<Skeleton className="h-9 w-40 motion-reduce:animate-none" />
			</div>
			<div>
				<div className="min-w-0 flex-1 space-y-6">
					<div data-skeleton-section="controls" className="mb-6 flex gap-4">
						<Skeleton className="h-9 w-32 motion-reduce:animate-none" />
						<Skeleton className="h-9 w-40 motion-reduce:animate-none" />
					</div>
					<div data-skeleton-section="content" className="space-y-6">
						{[0, 1, 2, 3, 4, 5].map((row) => (
							<div key={row} className="grid grid-cols-4 gap-4">
								<Skeleton className="col-span-2 h-5 motion-reduce:animate-none" />
								<Skeleton className="h-5 motion-reduce:animate-none" />
								<Skeleton className="h-5 motion-reduce:animate-none" />
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
